import React, { useState } from 'react';

const HowItWorks = ({ onBackToHome }) => {
  const [selectedDriver, setSelectedDriver] = useState(null);

  // The seven key drivers of mortgage rates
  const rateDrivers = [
    {
      id: 'fed-funds',
      icon: '🏦',
      title: 'Fed Funds Rate',
      subtitle: 'Federal Reserve Policy Rate',
      influence: 'Medium–High',
      influenceColor: 'text-orange-600',
      description: 'The overnight lending rate set by the Federal Reserve',
      whatItIs: 'The Fed Funds Rate is the interest rate at which banks lend money to each other overnight. It\'s the primary tool the Federal Reserve uses to influence economic activity.',
      whyItMatters: 'This rate affects short-term borrowing costs throughout the economy and signals the Fed\'s policy direction. When the Fed raises rates, it typically leads to higher mortgage rates, though the relationship isn\'t always direct.',
      currentImpact: 'Changes in the Fed Funds Rate can move mortgage rates by 0.5-1.5 percentage points, especially for adjustable-rate mortgages and short-term loans.',
      examples: [
        'Fed raises rates by 0.25% → Mortgage rates typically rise by 0.25-0.50%',
        'Fed signals hawkish policy → Markets price in future rate increases',
        'Fed pauses rate hikes → Mortgage rate volatility often decreases'
      ]
    },
    {
      id: 'inflation',
      icon: '📈',
      title: 'Inflation (CPI)',
      subtitle: 'Consumer Price Index',
      influence: 'Very High',
      influenceColor: 'text-red-600',
      description: 'Measures price increases across the economy',
      whatItIs: 'The Consumer Price Index tracks the average change in prices paid by consumers for goods and services over time. It\'s the most widely watched inflation measure.',
      whyItMatters: 'High inflation erodes the purchasing power of money over time. Lenders demand higher interest rates to compensate for this risk, making inflation one of the strongest predictors of mortgage rate direction.',
      currentImpact: 'For every 1% increase in inflation expectations, mortgage rates typically rise by 0.75-1.25 percentage points.',
      examples: [
        'CPI rises 0.5% monthly → Markets expect Fed rate hikes → Mortgage rates jump',
        'Core inflation stays elevated → Long-term rate expectations increase',
        'Inflation falls toward 2% target → Rate pressure moderates'
      ]
    },
    {
      id: 'treasury-10y',
      icon: '💵',
      title: '10-Year Treasury Yield',
      subtitle: 'Government Bond Benchmark',
      influence: 'Extremely High',
      influenceColor: 'text-red-700',
      description: 'The yield on U.S. government debt over 10 years',
      whatItIs: 'The 10-Year Treasury is considered the risk-free benchmark for long-term U.S. interest rates. It represents what the government pays to borrow money for 10 years.',
      whyItMatters: 'Mortgage rates typically track Treasury yields closely because both are long-term fixed-rate instruments. The Treasury provides the baseline "risk-free" rate, and mortgages add a premium on top.',
      currentImpact: 'Mortgage rates usually move 0.8-1.2 times the movement in 10-Year Treasuries. If Treasuries rise 0.5%, mortgages typically rise 0.4-0.6%.',
      examples: [
        '10Y Treasury: 4.0% → 30Y Mortgage: ~5.5-6.0%',
        'Treasury yields spike on economic data → Mortgage rates follow within hours',
        'Flight-to-safety lowers Treasuries → Mortgage rates decline'
      ]
    },
    {
      id: 'mbs-spread',
      icon: '🧮',
      title: 'MBS Spread',
      subtitle: 'Mortgage-Backed Securities Premium',
      influence: 'High',
      influenceColor: 'text-orange-600',
      description: 'Premium investors demand over Treasuries for mortgage risk',
      whatItIs: 'The MBS spread is the additional yield investors require to buy mortgage-backed securities instead of risk-free Treasury bonds. It reflects the credit and prepayment risk of mortgages.',
      whyItMatters: 'This spread directly affects mortgage rates beyond Treasury movements. When investors become nervous about mortgage credit quality or prepayment risk, they demand higher spreads, raising mortgage rates even if Treasuries stay flat.',
      currentImpact: 'MBS spreads typically range from 1.0-2.5% above Treasuries. During stress, spreads can widen to 3-4%, adding significantly to mortgage costs.',
      examples: [
        'Normal market: MBS spread = 1.5% → Manageable mortgage rates',
        'Credit concerns: MBS spread widens to 2.5% → Mortgage rates spike',
        'Fed MBS purchases: Spread tightens → Mortgage rates fall below normal relationship'
      ]
    },
    {
      id: 'quantitative-tightening',
      icon: '🧯',
      title: 'Quantitative Tightening (QT)',
      subtitle: 'Fed Balance Sheet Reduction',
      influence: 'Medium–High',
      influenceColor: 'text-orange-600',
      description: 'The Fed reducing its bond holdings',
      whatItIs: 'Quantitative Tightening occurs when the Federal Reserve reduces its balance sheet by selling bonds or letting them mature without reinvestment. It\'s the opposite of Quantitative Easing (QE).',
      whyItMatters: 'The Fed is typically the largest buyer of Treasury and mortgage-backed securities. When it stops buying or starts selling, it removes a major source of demand, pushing yields higher.',
      // Was: "Current QT pace allows up to $60B/month in Treasury runoff and
      // $35B/month in MBS runoff." Those caps are an FOMC policy setting that
      // changes, and this page has no data source, so it no longer states a
      // figure as current. The relationship it describes is the durable part.
      currentImpact: 'The Fed caps how much of its portfolio can run off each month. The higher that cap, the more supply the market must absorb without the Fed as a buyer, which can add roughly 0.25-0.75% to long-term rates. See Live Market Data for the current runoff caps.',
      examples: [
        'Fed announces QT → Bond yields rise in anticipation',
        'QT reaches full pace → Sustained upward pressure on rates',
        'Fed pauses QT during stress → Rate pressure moderates'
      ]
    },
    {
      id: 'global-pressure',
      icon: '🌍',
      title: 'Global/Economic Pressure',
      subtitle: 'Geopolitical & Crisis Events',
      influence: 'Variable',
      influenceColor: 'text-yellow-600',
      description: 'Wars, pandemics, political uncertainty, recession fears',
      whatItIs: 'Global events include geopolitical conflicts, pandemics, political instability, trade wars, and economic crises that create uncertainty in financial markets.',
      whyItMatters: 'During uncertain times, investors often flee to the safety of U.S. Treasury bonds, driving yields down and potentially lowering mortgage rates. However, if events threaten the U.S. economy directly, the opposite can occur.',
      currentImpact: 'Impact is highly variable and can be dramatic. Flight-to-safety can drop mortgage rates by 0.5-1.5% overnight, while U.S.-specific crises can spike them similarly.',
      examples: [
        'European banking crisis → Flight to U.S. bonds → Lower mortgage rates',
        'Trade war escalation → Economic uncertainty → Rate volatility',
        'Pandemic outbreak → Fed emergency cuts + bond buying → Mortgage rates plummet'
      ]
    },
    {
      id: 'unemployment',
      icon: '👥',
      title: 'Unemployment Rate',
      subtitle: 'Labor Market Health',
      influence: 'Medium',
      influenceColor: 'text-yellow-600',
      description: 'Percentage of labor force unemployed but seeking work',
      whatItIs: 'The unemployment rate measures the percentage of people in the labor force who are jobless but actively seeking employment. It\'s a key indicator of economic health.',
      whyItMatters: 'Rising unemployment typically signals economic weakness, prompting the Fed to lower rates to stimulate job growth. Conversely, very low unemployment can indicate an overheating economy, leading to rate increases.',
      currentImpact: 'Unemployment changes affect Fed policy expectations. A 1% rise in unemployment often correlates with 1-2% reduction in Fed Funds Rate over time.',
      examples: [
        'Unemployment spikes to 8% → Fed cuts rates aggressively → Mortgage rates fall',
        'Unemployment drops to 3% → Wage inflation concerns → Fed raises rates',
        'Jobless claims surge → Recession fears → Bond yields and mortgage rates decline'
      ]
    }
  ];

  const getInfluenceColor = (influence) => {
    switch (influence) {
      case 'Extremely High': return 'bg-red-100 text-red-700 border-red-300';
      case 'Very High': return 'bg-red-100 text-red-700 border-red-300';
      case 'High': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'Medium–High': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'Variable': return 'bg-purple-100 text-purple-700 border-purple-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackToHome}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <span>←</span>
              <span>Back to Home</span>
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900">How Mortgage Rates Really Move</h1>
              <p className="text-lg text-gray-600 mt-2">
                Understanding the seven key drivers that determine your mortgage rate
              </p>
            </div>
            <div className="w-24"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Introduction */}
        <div className="mb-12 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-4">
            <span className="text-3xl">🎓</span>
            <div>
              <h2 className="text-xl font-semibold text-blue-900 mb-3">
                Why Understanding Rate Drivers Matters
              </h2>
              <p className="text-blue-800 leading-relaxed">
                Mortgage rates don't move randomly. They respond to specific economic forces that you can track and understand. 
                By learning these seven key drivers, you'll be able to anticipate rate movements, time your mortgage decisions better, 
                and understand why rates change when they do. Each driver affects rates differently — some create immediate impacts, 
                while others build pressure over time.
              </p>
            </div>
          </div>
        </div>

        {/* Rate Drivers Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
          {rateDrivers.map((driver) => (
            <div
              key={driver.id}
              className={`bg-white rounded-lg shadow-md border p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                selectedDriver?.id === driver.id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              }`}
              onClick={() => setSelectedDriver(selectedDriver?.id === driver.id ? null : driver)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{driver.icon}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{driver.title}</h3>
                    <p className="text-sm text-gray-600">{driver.subtitle}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getInfluenceColor(driver.influence)}`}>
                  {driver.influence}
                </span>
              </div>

              {/* Description */}
              <p className="text-gray-700 text-sm mb-4">{driver.description}</p>

              {/* Click to expand indicator */}
              <div className="text-center">
                <span className="text-xs text-blue-600 font-medium">
                  {selectedDriver?.id === driver.id ? '▼ Click to collapse' : '▶ Click to learn more'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Explanation Panel */}
        {selectedDriver && (
          <div className="mb-12 p-8 bg-white rounded-lg shadow-lg border border-gray-200 animate-pulse-once">
            <div className="flex items-center space-x-4 mb-6">
              <span className="text-4xl">{selectedDriver.icon}</span>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedDriver.title}</h2>
                <p className="text-lg text-gray-600">{selectedDriver.subtitle}</p>
                <span className={`inline-block mt-2 px-3 py-1 text-sm font-medium rounded-full border ${getInfluenceColor(selectedDriver.influence)}`}>
                  Influence: {selectedDriver.influence}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">📝 What It Is</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedDriver.whatItIs}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 Why It Matters</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedDriver.whyItMatters}</p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Current Impact</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedDriver.currentImpact}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 Real Examples</h3>
                  <ul className="space-y-2">
                    {selectedDriver.examples.map((example, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span className="text-gray-700 text-sm">{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Methodology Section */}
        <div className="mb-12 p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-4 mb-6">
            <span className="text-3xl">🔬</span>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Our Methodology</h2>
              <p className="text-lg text-gray-600">How RateLens analyzes and predicts mortgage rate movements</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">📡</span>
                Real-Time Data
              </h3>
              <p className="text-gray-700 text-sm">
                We pull live data from Federal Reserve Economic Data (FRED), Treasury.gov, and major market data providers. 
                Our system updates every 5 minutes during market hours.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">🧮</span>
                Mathematical Models
              </h3>
              <p className="text-gray-700 text-sm">
                Our algorithms analyze historical correlations between each driver and mortgage rates, weighting their relative 
                importance based on current market conditions and economic cycles.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">🤖</span>
                AI Enhancement
              </h3>
              <p className="text-gray-700 text-sm">
                Machine learning models identify patterns and relationships that traditional analysis might miss, 
                providing more nuanced predictions and scenario analysis.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">📊</span>
                Historical Analysis
              </h3>
              <p className="text-gray-700 text-sm">
                We analyze decades of historical data to understand how these drivers have affected rates during different 
                economic conditions, market cycles, and policy regimes.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">🎯</span>
                Scenario Testing
              </h3>
              <p className="text-gray-700 text-sm">
                Our simulator allows you to adjust each driver independently to see how different economic scenarios 
                would affect mortgage rates, helping you understand potential future outcomes.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">✅</span>
                Validation
              </h3>
              <p className="text-gray-700 text-sm">
                We continuously validate our models against actual market movements and adjust our algorithms to 
                maintain accuracy and relevance in changing market conditions.
              </p>
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <div className="p-6 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center space-x-3 mb-4">
            <span className="text-2xl">🎯</span>
            <h3 className="text-lg font-semibold text-green-900">Key Takeaways</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-800">
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-1">•</span>
                <span><strong>10-Year Treasury</strong> is the strongest predictor of mortgage rate direction</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-1">•</span>
                <span><strong>Inflation data</strong> often triggers immediate rate movements</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-1">•</span>
                <span><strong>Fed policy</strong> provides the foundation for rate expectations</span>
              </li>
            </ul>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-1">•</span>
                <span><strong>Global events</strong> can override all other factors temporarily</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-1">•</span>
                <span><strong>MBS spreads</strong> affect rates beyond Treasury movements</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-1">•</span>
                <span><strong>Multiple factors</strong> usually move together, amplifying effects</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
